// utils/useProducts.ts
'use client';

import useSWR from 'swr';
import type { ProductSearchBody, Product } from '@/models/ProductTypes';

/** Keep the payload tight: only include defined keys we support */
function buildBody(input: ProductSearchBody): ProductSearchBody {
  const { searchTerm, itemCategory, minPrice, maxPrice, sort, onlyDiscounted } = input || {};
  const body: ProductSearchBody = {};
  if (searchTerm) body.searchTerm = searchTerm;
  if (itemCategory) body.itemCategory = itemCategory;
  if (minPrice !== undefined) body.minPrice = minPrice;
  if (maxPrice !== undefined) body.maxPrice = maxPrice;
  if (sort) body.sort = sort;
  if (onlyDiscounted) body.onlyDiscounted = onlyDiscounted;
  return body;
}

/** SWR array key -> fetcher receives the whole key as a single arg */
type SWRKey = [url: string, body: ProductSearchBody];

const fetcher = async ([url, body]: SWRKey): Promise<Product[]> => {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(buildBody(body)),
  });
  if (!res.ok) {
    throw new Error(`Search failed: ${res.status}`);
  }
  const json = await res.json();
  // Your API returns an array of products directly
  return Array.isArray(json) ? (json as Product[]) : [];
};

export function useProducts(filters: ProductSearchBody) {
  const key: SWRKey = ['/api/store/search', filters];
  const { data, error, isLoading, mutate } = useSWR<Product[], Error, SWRKey>(key, fetcher, { keepPreviousData: true });

  return {
    products: data ?? [],
    error,
    isLoading,
    mutate,
  };
}
